import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { FieldOptionFetchResponse, FieldType } from 'types/FieldTypes';
import SwitchButton from 'components/buttons/SwitchButton';
import FeatureBadge from 'components/billing/FeatureBadge';
import FormFieldContext from 'components/forms/FormFieldContext';
import { EnvironmentSettingsType } from 'services/settings/SettingsTypes';
import { generateFieldComponents } from 'components/forms/GenerateFieldComponents';
import { saveData } from 'services/api/saveData';
import { useGlobalContext } from 'GlobalContext';
import { useFetchData } from 'services/api/useFetchData';
import { useSettings } from 'services/settings/SettingsContext';
import { encryptData } from 'services/authentication/encryptData';
import { fetchData } from 'services/api/fetchData';
import { useSettingsDivision } from './SettingsDivisionContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/settings.scss';

export interface SettingsSettingType {
    title: string;
    switchButton?: {
        enabled: boolean;
        name: string;
        featureSwitch?: boolean;
    };
    explanationText?: string;
    tooltipText?: string; // Shown behind a question icon next to the title
    requiredFeature?: string;
    dependentOn?: { fieldName: string, value: string | string[] | boolean };
    scope?: 'environment' | 'division';
    fields?: FieldType[];
    subSetting?: boolean;
}

export interface SettingsContainerType {
    settings: SettingsSettingType[];
    environmentScope?: boolean;
    infoText?: string;
}

const SettingsContainer: React.FC<SettingsContainerType> = ({ settings, environmentScope, infoText }) => {
    const { t } = useTranslation();
    const viewKey = useMemo(() => uuidv4(), []);
    const { packageFeatures, activeFeatures, environmentHash, handleLogout } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const { setEnvironmentSettings } = useSettings();
    const { selectedDivision, isEnvironmentSelected } = useSettingsDivision();

    // Fetch the environment settings with the division values overlayed when another division is selected
    const fetchParams = useMemo(() => (
        !environmentScope && !isEnvironmentSelected && selectedDivision ? { division: selectedDivision.id } : undefined
    ), [environmentScope, isEnvironmentSelected, selectedDivision?.id]);
    const { response: fetchedEnvironmentSettings, loading } = useFetchData({ apiUrl: `get_environment_settings/${environmentHash}`, params: fetchParams });

    // Determine the patch url based on the selected division
    const patchApiUrl = !environmentScope && !isEnvironmentSelected && selectedDivision
        ? `patch_division_settings/${selectedDivision.id}`
        : `patch_environment_settings/${environmentHash}`;

    // Only show the division scoped settings when another division is selected
    // Containers with the environment scope always show all settings
    const visibleSettings = useMemo(() => (
        environmentScope || isEnvironmentSelected ? settings : settings.filter(setting => setting.scope === 'division')
    ), [settings, environmentScope, isEnvironmentSelected]);
    const [data, setData] = useState<EnvironmentSettingsType>();
    const [dropdownData, setDropdownData] = useState<Record<string, FieldOptionFetchResponse>>({});
    const [editing, setEditing] = useState(false);
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [buttonLoader, setButtonLoader] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [toggleSettings, setToggleSettings] = useState<Record<string, boolean>>({});

    // Set the fetched settings in the data
    useEffect(() => {
        if (fetchedEnvironmentSettings) setData(fetchedEnvironmentSettings);
    }, [fetchedEnvironmentSettings]);

    // Fetch the dropdown data if an api object is given for the field
    useEffect(() => {
        const fetchDropdownData = async () => {
            const dropdownData: Record<string, FieldOptionFetchResponse> = {};
            for (let setting of settings) {
                const fields = setting.fields || [];

                for (let field of fields) {

                    // Don't execute fetches for fields which are not shown
                    if ('activeFeature' in field && field.activeFeature) {
                        if (!activeFeatures.includes(field.activeFeature)) {
                            continue;
                        }
                    }

                    // Fetch by api request object
                    if (field.type === 'dropdown' && field.apiRequest) {
                    
                        // Get the request config
                        const request = field.apiRequest[0];
    
                        // Fetch the data
                        const results = await fetchData({ 
                            apiUrl: request.endpoint,
                            params: request.params,
                            handleLogout 
                        });
    
                        // Store the results
                        dropdownData[request.object] = results;
                    }
                };
            }
            setDropdownData(dropdownData);
        };
        fetchDropdownData();
    }, []);

    // Render setting items based on its dependent on value
    const showSetting = (setting: SettingsSettingType): boolean => {
        if (data && setting.dependentOn) {
            const { fieldName, value } = setting.dependentOn;

            const fieldKey = fieldName as keyof EnvironmentSettingsType;

            // Check if the dependent field exists in the data or updated data
            const dependentFieldExists = (data && fieldKey in data) || (updatedData && fieldKey in updatedData);

            if (!dependentFieldExists) {
                return false;
            } else {
                // Check for the most recent value between updated data and fetched data
                let dependentFieldValue = updatedData[fieldKey] ?? data[fieldKey];

                // Determine if the setting has to be shown
                if (typeof value === 'boolean') {
                    if (dependentFieldValue !== value) {
                        return false;
                    }
                } else if (Array.isArray(value)) {
                    return Array.isArray(dependentFieldValue)
                        ? value.some(val => dependentFieldValue.includes(val))
                        : value.includes(dependentFieldValue);
                } else {
                    return Array.isArray(dependentFieldValue)
                        ? dependentFieldValue.includes(value)
                        : dependentFieldValue === value;
                }
            }
        }
        return true;
    };    

    // Handle the toggling of fields if the setting switch is on or off
    const handleToggle = (setting: SettingsSettingType, isOn: boolean) => {
        setToggleSettings(prev => ({...prev, [setting.title]: isOn }));

        // Also mirror the switch value into the updated data, so other settings can depend on it
        if (setting.switchButton) {
            setUpdatedData(prev => ({...prev, [setting.switchButton!.name]: isOn }));
        }
    };

    // Render the fields
    const renderFields = useCallback((fields: FieldType[]) => {
        if (viewKey && fetchedEnvironmentSettings) {
            // Set the view in edit mode and save on blur parameters to true for all fields
            const setFields = fields.map(field => ({ ...field, viewInEditMode: true, saveOnBlur: true }));

            // Generate the fields
            return generateFieldComponents(viewKey, setFields, dropdownData, undefined, fetchedEnvironmentSettings, updatedData, activeFeatures)
        }
    }, [fetchedEnvironmentSettings, dropdownData, updatedData]);

    // Handle submit
    const handleSubmit = async (newData?: Record<string, any>) => {
        // Data to submit is either the given new data or else the updated data
        const submitData = newData || updatedData;

        try {
            const response = await saveData({ apiUrl: patchApiUrl, method: 'patch', data: submitData });

            // Handle succesful response
            if (response?.status === 200) {

                // Only update the session settings state when the main division is edited
                if (isEnvironmentSelected) {

                    // Update the environment settings state
                    setEnvironmentSettings(response.data);

                    // Convert the environment settings to a string and store it encrypted in the local storage
                    const settingsString = JSON.stringify(response.data);
                    localStorage.setItem('env_settings', encryptData(settingsString))
                }

                // Show success alert
                setFloatingAlert({ type: 'success' })
            }
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <FormFieldContext.Provider value={{ editing, setEditing, updatedData, setUpdatedData, buttonLoader, 
            setButtonLoader, showErrorAlert, setShowErrorAlert, handleSubmit }}>
            <div className='container-filled'>
                {loading === 'show-loader' ? (
                    <div className='inline-form-loader'>
                        <div className="loader"></div>
                    </div>
                ) : loading === 'success' ? (
                    <div className='section settings-form'>
                        {infoText && (
                            // Render an info alert at the top of the settings card
                            <div className='alert alert-warning settings-info-alert' role='alert'>
                                <FontAwesomeIcon icon={faCircleExclamation} />
                                <span>{t(infoText)}</span>
                            </div>
                        )}
                        {visibleSettings.map((setting, index) => {
                            // Determine if the setting is disabled because the feature isn't included in the current package
                            const isSettingDisabled = setting.requiredFeature ? !packageFeatures.includes(setting.requiredFeature) : false;

                            // Determine to show the setting in case its dependent on a value of another setting
                            if (showSetting(setting)) {
                                return (
                                    <div key={index} className={`setting-item ${setting.subSetting ? 'sub-setting' : ''}`}>
                                        {setting.switchButton && (
                                            <div className='switch-button-wrapper'>
                                                {setting.switchButton.featureSwitch ? (
                                                    // Render a feature switch which toggles an environment feature
                                                    <SwitchButton
                                                        key={setting.switchButton.name}
                                                        featureSwitch={true}
                                                        isDisabled={isSettingDisabled}
                                                        name={setting.switchButton.name}
                                                        apiUrl='patch_environment_feature'
                                                        onToggle={(isOn) => handleToggle(setting, isOn)}
                                                    />
                                                ) : (
                                                    // Render a normal settings switch which patches a boolean setting
                                                    <SwitchButton
                                                        key={setting.switchButton.name}
                                                        isDisabled={isSettingDisabled}
                                                        name={setting.switchButton.name}
                                                        data={data}
                                                        apiUrl={patchApiUrl}
                                                        onToggle={(isOn) => handleToggle(setting, isOn)}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        <div>
                                            <div className={`title ${setting.switchButton ? 'switch' : ''} `}>
                                                <h5>{t(setting.title)}</h5>
                                                {setting.tooltipText && (
                                                    // Show the tooltip text behind a question icon
                                                    <span className='tooltip-icon settings-title-tooltip'>
                                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                                        <span className='tooltip tooltip-right'>{t(setting.tooltipText)}</span>
                                                    </span>
                                                )}
                                                {isSettingDisabled && (
                                                    // If the setting is disabled, show the order module or upgrade badge
                                                    <FeatureBadge feature={setting.requiredFeature} />
                                                )}
                                            </div>
                                            <div className='content'>
                                                {(!setting.switchButton || toggleSettings[setting.title]) && 
                                                    // Render tab fields on top of the explanation text. If the setting has a switch button, only show the field if the switch is turned on
                                                    renderFields(setting.fields?.filter(field => field.type === 'tabs') || [])
                                                }
                                                {setting.explanationText && (
                                                    // Render an explanation text if it is set
                                                    <div className='col-100'>
                                                        <div className='col-70'>
                                                            <p className='explanation-text'>
                                                                {t(setting.explanationText)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                                {(!setting.switchButton || toggleSettings[setting.title]) && 
                                                    // Render other fields below the explanation text. If the setting has a switch button, only show the field if the switch is turned on
                                                    renderFields(setting.fields?.filter(field => field.type !== 'tabs') || [])
                                                }
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                ): null}
            </div>
        </FormFieldContext.Provider>
    );
};

export default SettingsContainer;